import { useEffect, useState } from 'react';
import axios from 'axios';
import { ClipboardCheck, Eye, PenLine, UserCheck } from 'lucide-react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { API_CP, Carte, TitreCarte, nomComplet, qualiteOrg } from './constantes';
import { SignatureEcranDialog } from './SignatureEcranDialog';

const OPTIONS = [['present', 'Présent', 'bg-emerald-600'], ['excuse', 'Excusé', 'bg-amber-500'], ['absent', 'Absent', 'bg-slate-500']];

const LignePresence = ({ p, sig, visio, onChange, onSigner }) => (
  <li className="grid gap-3 py-4 md:grid-cols-[1.2fr_auto_1fr] md:items-center" data-testid={`cp-presence-${p.participant_id}`}>
    <div className="min-w-0">
      <p className="truncate text-sm font-semibold text-[#0F172A]">{nomComplet(p)}</p>
      <p className="truncate text-xs text-slate-500">{qualiteOrg(p)}</p>
    </div>
    <div className="flex flex-wrap items-center gap-3">
      <div className="inline-flex rounded-full bg-slate-100 p-1">
        {OPTIONS.map(([v, l, actif]) => (
          <button key={v} type="button" onClick={() => onChange({ presence: p.presence === v ? '' : v })} data-testid={`cp-presence-${p.participant_id}-${v}`}
            className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors duration-200 ${p.presence === v ? `${actif} text-white` : 'text-slate-600 hover:bg-white'}`}>{l}</button>
        ))}
      </div>
      {visio && p.presence === 'present' && (
        <label className="flex cursor-pointer items-center gap-1.5 text-xs text-slate-600">
          <Checkbox checked={!!p.a_distance} onCheckedChange={(v) => onChange({ a_distance: !!v })} data-testid={`cp-presence-visio-${p.participant_id}`} /> En visio
        </label>
      )}
    </div>
    <div className="flex items-center gap-3 md:justify-end">
      {sig
        ? <img src={sig} alt="Signature" className="h-12 max-w-[140px] object-contain" data-testid={`cp-signature-${p.participant_id}`} />
        : <span className="text-xs text-slate-400" data-testid={`cp-signature-absente-${p.participant_id}`}>Aucune signature enregistrée</span>}
      <Button size="sm" variant="outline" onClick={onSigner} data-testid={`cp-signer-${p.participant_id}`}>
        <PenLine className="mr-1.5 h-3.5 w-3.5" /> {sig ? 'Re-signer' : 'Signer à l\'écran'}
      </Button>
    </div>
  </li>
);

export const OngletPresences = ({ cp, onPdf }) => {
  const { conseil, enregistrer, setConseil } = cp;
  const [sigs, setSigs] = useState({});
  const [aSigner, setASigner] = useState(null);
  const api = API_CP(conseil.annee);
  useEffect(() => {
    axios.get(`${api}/signatures`).then(({ data }) => setSigs(data)).catch(() => setSigs({}));
  }, [api, conseil.updated_at]);
  const sauver = (parts, msg = null) => { setConseil((c) => ({ ...c, participants: parts })); enregistrer({ participants: parts }, msg); };
  const maj = (pid, champs) => sauver(conseil.participants.map((p) => (p.participant_id === pid ? { ...p, ...champs } : p)));
  const presents = conseil.participants.filter((p) => p.presence === 'present').length;

  return (
    <Carte data-testid="cp-presences">
      <TitreCarte Icone={ClipboardCheck} titre={`Présences et signatures (${presents} présent${presents > 1 ? 's' : ''})`}
        sousTitre="Indiquez qui était présent : la signature enregistrée dans le profil de chacun est reprise sur la feuille d'émargement et le procès-verbal.">
        <Button variant="outline" onClick={() => sauver(conseil.participants.map((p) => ({ ...p, presence: p.presence || 'present' })), 'Présences enregistrées')} data-testid="cp-presences-tous">
          <UserCheck className="mr-2 h-4 w-4" /> Tous présents
        </Button>
        <Button onClick={() => onPdf(null, "Feuille d'émargement", `${api}/emargement.pdf`)} className="bg-[#0F172A]" data-testid="cp-emargement-pdf">
          <Eye className="mr-2 h-4 w-4" /> Feuille d'émargement
        </Button>
      </TitreCarte>
      <ul className="divide-y">
        {conseil.participants.map((p) => (
          <LignePresence key={p.participant_id} p={p} sig={sigs[p.participant_id]} visio={conseil.mode !== 'presentiel'}
            onChange={(ch) => maj(p.participant_id, ch)} onSigner={() => setASigner(p)} />
        ))}
      </ul>
      <SignatureEcranDialog participant={aSigner} annee={conseil.annee} onClose={() => setASigner(null)} onSigne={(data) => { setConseil(data); setASigner(null); }} />
    </Carte>
  );
};
